import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

const SESSION_KEY = "rym_session_id";
const FLUSH_INTERVAL = 5000;
const MAX_BATCH_SIZE = 50;

interface QueuedEvent {
  event_name: string;
  event_category: string;
  event_data: { [key: string]: Json | undefined };
  page_path: string;
  session_id: string;
  user_id: string;
  created_at: string;
}

const eventQueue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let currentUserId: string | null = null;

function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export function setTrackingUserId(userId: string | null) {
  currentUserId = userId;
}

export function trackEvent(
  name: string,
  category: "engagement" | "navigation" | "conversion" | "feature" | "interaction" = "engagement",
  data?: { [key: string]: Json | undefined }
) {
  if (!currentUserId) return; // Only track authenticated users

  const event: QueuedEvent = {
    event_name: name,
    event_category: category,
    event_data: data || {},
    page_path: window.location.pathname,
    session_id: getSessionId(),
    user_id: currentUserId,
    created_at: new Date().toISOString(),
  };

  eventQueue.push(event);

  if (eventQueue.length >= MAX_BATCH_SIZE) {
    flushEvents();
  }

  if (!flushTimer) {
    flushTimer = setInterval(flushEvents, FLUSH_INTERVAL);
  }
}

async function flushEvents() {
  if (eventQueue.length === 0) return;

  const batch = eventQueue.splice(0, MAX_BATCH_SIZE);

  try {
    await supabase.from("user_events").insert(batch);
  } catch (e) {
    // Silent fail — never block the UI
    console.debug("[event-tracking] flush failed:", e);
  }
}

// Flush on page unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    if (eventQueue.length > 0) {
      const batch = eventQueue.splice(0);
      navigator.sendBeacon(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_events`,
        JSON.stringify(batch)
      );
    }
  });
}

// ── Scroll depth tracking ──────────────────────────────
const scrollMilestones = new Set<number>();

export function initScrollTracking() {
  scrollMilestones.clear();

  const handler = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const pct = Math.round((scrollTop / docHeight) * 100);

    for (const milestone of [25, 50, 75, 100]) {
      if (pct >= milestone && !scrollMilestones.has(milestone)) {
        scrollMilestones.add(milestone);
        trackEvent("scroll_depth", "engagement", { depth: milestone });
      }
    }
  };

  window.addEventListener("scroll", handler, { passive: true });
  return () => window.removeEventListener("scroll", handler);
}

// Cleanup
export function stopTracking() {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flushEvents();
}
